import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import Payment from 'src/smoothr-web-app-core/models/Payment';
import Venue from 'src/smoothr-web-app-core/models/Venue';
import { environment } from '../../../environments/environment';
import { Api } from 'src/smoothr-web-app-core/api/api';
import { TranslateService } from '@ngx-translate/core';

const GP_SCRIPT = 'https://js.globalpay.com/4.1.3/globalpayments.js';

@Component({
	selector: 'app-eservice-google-pay',
	templateUrl: './eservice-google-pay.component.html',
	styleUrls: ['./eservice-google-pay.component.scss'],
})
export class EserviceGooglePayComponent implements OnInit, OnDestroy {
	@Input() payment: Payment;
	@Input() venue: Venue;
	@Output() paymentSuccess = new EventEmitter<Payment>();
	@Output() paymentError = new EventEmitter<string>();

	statusMessage = '';
	isSubmitting = false;
	showGooglePay = false;

	private apmFormRef: any = null;
	private initialized = false;

	constructor(
		private cdr: ChangeDetectorRef,
		private translate: TranslateService
	) {}

	ngOnInit() {
		this.statusMessage = this.translate.instant('eservice_gpay.status.initializing');
		this.initialize();
	}

	ngOnDestroy() {
		this.initialized = false;
		this.apmFormRef = null;
	}

	private async initialize() {
		try {
			const accessToken = this.extractAccessToken(this.payment);
			if (!accessToken) {
				this.statusMessage = this.translate.instant('eservice_gpay.status.no_token');
				this.cdr.detectChanges();
				return;
			}
			this.showGooglePay = true;
			this.statusMessage = this.translate.instant('eservice_gpay.status.initializing');
			this.cdr.detectChanges();
			await this.initializeGooglePay(accessToken);
		} catch (e: any) {
			this.statusMessage = e?.message || this.translate.instant('eservice_gpay.status.init_error');
			this.cdr.detectChanges();
		}
	}

	private extractAccessToken(payload: any): string {
		return (
			payload?.accessToken ||
			payload?.access_token ||
			payload?.token ||
			payload?.eServicePayment?.accessToken ||
			payload?.eServicePayment?.access_token ||
			''
		);
	}

	private async initializeGooglePay(accessToken: string) {
		await this.loadScript(GP_SCRIPT);
		const gp = (window as any).GlobalPayments;
		if (!gp || this.initialized) {
			return;
		}

		const cfg = (environment as any).eServiceGPay ?? {};
		const currencyCode = this.venue?.currency || 'EUR';
		const countryCode = (this.venue?.country || 'DE').toUpperCase();

		gp.configure({
			accessToken,
			apiVersion: '2021-03-22',
			env: environment.production ? 'production' : 'sandbox',
			apms: {
				currencyCode,
				allowedCardNetworks: ['VISA', 'MASTERCARD'],
				googlePay: {
					currencyCode,
					countryCode,
					merchantName: this.venue?.name || 'Merchant',
					allowedAuthMethods: ['PAN_ONLY'],
					allowedCardNetworks: ['VISA', 'MASTERCARD'],
					buttonType: 'pay',
					buttonColor: 'black',
					merchantId: cfg.googleMerchantId || '',
					globalPaymentsClientID: cfg.clientId || '',
				},
			},
		});

		const rawSum = (this.payment.sum as any)?.$numberDecimal ?? this.payment.sum;
		const apmForm = gp.apm.form('#eservice-google-pay-container', {
			amount: Number(rawSum).toFixed(2),
			apms: ['google-pay'],
		});

		this.apmFormRef = apmForm;
		this.initialized = true;

		apmForm.ready(() => {
			this.statusMessage = this.translate.instant('eservice_gpay.status.ready');
			this.cdr.detectChanges();
		});

		apmForm.on('token-success', async (resp: any) => {
			const paymentReference = resp?.paymentReference || '';
			this.isSubmitting = true;
			this.statusMessage = this.translate.instant('eservice_gpay.status.charging');
			this.cdr.detectChanges();

			try {
				const result = await Api.eServicePay(this.payment._id, paymentReference);
				this.statusMessage = this.translate.instant('eservice_gpay.status.success');
				this.cdr.detectChanges();
				this.paymentSuccess.emit({ ...this.payment, ...result });
			} catch (e: any) {
				const msg = e?.message || this.translate.instant('eservice_gpay.status.charge_failed');
				this.statusMessage = msg;
				this.paymentError.emit(msg);
			} finally {
				this.isSubmitting = false;
				this.cdr.detectChanges();
			}
		});

		apmForm.on('token-error', (resp: any) => {
			const errorList = resp?.error?.error?.detailedErrorDescription
				? [resp.error.error.detailedErrorDescription]
				: Array.isArray(resp?.reasons)
					? resp.reasons.map((r: any) => r?.message || JSON.stringify(r))
					: [];
			this.statusMessage = errorList.length ? errorList.join(' | ') : this.translate.instant('eservice_gpay.status.tokenize_failed');
			this.isSubmitting = false;
			this.cdr.detectChanges();
		});
	}

	private loadScript(src: string): Promise<void> {
		return new Promise((resolve, reject) => {
			if (document.querySelector(`script[src="${src}"]`)) {
				resolve();
				return;
			}
			const s = document.createElement('script');
			s.src = src;
			s.async = true;
			s.onload = () => resolve();
			s.onerror = () => reject(new Error(`Failed to load: ${src}`));
			document.body.appendChild(s);
		});
	}
}
